import {
    Cartesian2,
    Color,
    DistanceDisplayCondition,
    HeightReference,
    HorizontalOrigin,
    LabelGraphics,
    LabelStyle,
    NearFarScalar,
    VerticalOrigin,
} from 'cesium';
import Cartesian3 from 'cesium/Source/Core/Cartesian3';
import React from 'react';
import { Entity, EntityDescription } from 'resium';
import mapMarkerPurple from '../../images/map-marker-alt-purple.png';
import mapMarkerBlue from '../../images/map-marker-alt-blue.png';
import mapMarkerGreen from '../../images/map-marker-alt-green.png';

export interface Hike {
	_createdAt: Date;
	_id: string;
	_rev: string;
	_type: string;
	_updatedAt: Date;
	date: Date;
	destination: Destination;
	distance: number;
	gallery: Gallery;
	location: Gallery;
	mainImage: MainImage;
	map: Gallery;
	maxEl: number;
	minEl: number;
	negEl: number;
	places: Destination[];
	posEl: number;
	startingPoint: Destination;
	tags: Tag[];
	title: string;
}

export interface Destination {
	height: number;
	image: Image;
	lat: number;
	link: string;
	lon: number;
	name: string;
}

export interface Image {
	asset: Asset;
}

export interface Asset {
	_createdAt: Date;
	_id: string;
	_rev: string;
	_type: string;
	_updatedAt: Date;
	assetId: string;
	extension: string;
	metadata: Metadata;
	mimeType: string;
	originalFilename: string;
	path: string;
	sha1hash: string;
	size: number;
	uploadId: string;
	url: string;
}

export interface Metadata {
	_type: string;
	dimensions: Dimensions;
	hasAlpha: boolean;
	isOpaque: boolean;
	lqip: string;
	palette: Palette;
}

export interface Dimensions {
	_type: string;
	aspectRatio: number;
	height: number;
	width: number;
}

export interface Palette {
	_type: string;
	darkMuted: DarkMuted;
	darkVibrant: DarkMuted;
	dominant: DarkMuted;
	lightMuted: DarkMuted;
	lightVibrant: DarkMuted;
	muted: DarkMuted;
	vibrant: DarkMuted;
}

export interface DarkMuted {
	_type: Type;
	background: string;
	foreground: Foreground;
	population: number;
	title: Foreground;
}

export enum Type {
	SanityImagePaletteSwatch = 'sanity.imagePaletteSwatch',
}

export enum Foreground {
	Fff = '#fff',
	The000 = '#000',
}

export interface Gallery {
	_ref: string;
	_type: string;
}

export interface MainImage {
	_type: string;
	alt: string;
	asset: Gallery;
	caption: string;
}

export interface Tag {
	_key: string;
	_ref: string;
	_type: string;
}

export const commonLabelProperties:
	| LabelGraphics
	| LabelGraphics.ConstructorOptions
	| undefined = {
	    font: '12px Helvetica',
	    heightReference: HeightReference.CLAMP_TO_GROUND,
	    horizontalOrigin: HorizontalOrigin.CENTER,
	    verticalOrigin: VerticalOrigin.BOTTOM,
	    fillColor: Color.WHITE,
	    showBackground: true,
	    backgroundColor: new Color(0, 0, 0),
	    backgroundPadding: new Cartesian2(8, 4),
	    distanceDisplayCondition: new DistanceDisplayCondition(0, 50000),
	    scaleByDistance: new NearFarScalar(0, 1.3, 50000, 0),
	    pixelOffsetScaleByDistance: undefined,
	    pixelOffset: new Cartesian2(0, -45),
	};

const commonBillboardProperties = {
    width: 100,
    height: 100,
    verticalOrigin: VerticalOrigin.BOTTOM,
    heightReference: HeightReference.CLAMP_TO_GROUND,
    scaleByDistance: new NearFarScalar(0, 1.0, 50000.0, 0),
};

const LinkButton: React.FC<{ link: string; text: string }> = ({
    link,
    text,
}) => (
    <button
        role="link"
        onClick={() => {
            window.open(link);
        }}
        style={{
            cursor: 'pointer',
            padding: '6px 10px',
            background: 'none',
            textDecoration: 'underline',
            color: '#2aff43',
            border: 'none',
            borderRadius: '8px',
            margin: '8px 0px',
        }}
    >
        {text}
    </button>
);

const PointEntities: React.FC<{ hike: Hike | null }> = ({ hike }) => (
    <>
        {hike && (
            <>
                <Entity
                    billboard={{
                        image: mapMarkerPurple,
                        ...commonBillboardProperties,
                    }}
                    label={{
                        text: hike.startingPoint.name,
                        ...commonLabelProperties,
                    }}
                    name={hike.startingPoint.name}
                    position={Cartesian3.fromDegrees(
                        hike.startingPoint.lon,
                        hike.startingPoint.lat
                    )}
                >
                    <EntityDescription>
                        {hike.startingPoint.image && (
                            <img
                                style={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    objectFit: 'contain',
                                }}
                                src={hike.startingPoint.image.asset.url}
                                alt=""
                            />
                        )}
                        {hike.startingPoint.link && (
                            <LinkButton
                                link={hike.startingPoint.link}
                                text={hike.startingPoint.name}
                            />
                        )}
                    </EntityDescription>
                </Entity>
                <Entity
                    billboard={{
                        image: mapMarkerGreen,
                        ...commonBillboardProperties,
                    }}
                    label={{
                        text: hike.destination.name,
                        ...commonLabelProperties,
                    }}
                    name={hike.destination.name}
                    position={Cartesian3.fromDegrees(
                        hike.destination.lon,
                        hike.destination.lat
                    )}
                >
                    <EntityDescription>
                        {hike.destination.image && (
                            <img
                                style={{
                                    maxWidth: '100%',
                                    height: 'auto',
                                    objectFit: 'contain',
                                }}
                                src={hike.destination.image.asset.url}
                                alt=""
                            />
                        )}
                        {hike.destination.link && (
                            <LinkButton
                                link={hike.destination.link}
                                text={hike.destination.name}
                            />
                        )}
                    </EntityDescription>
                </Entity>
                {hike.places &&
					hike.places.length > 0 &&
					hike.places.map((pl) => {
					    if (pl.lon && pl.lat) {
					        return (
					            <Entity
					                billboard={{
					                    image: mapMarkerBlue,
					                    ...commonBillboardProperties,
					                }}
					                label={{
					                    text: pl.name,
					                    ...commonLabelProperties,
					                }}
					                position={Cartesian3.fromDegrees(
					                    pl.lon,
					                    pl.lat
					                )}
					                name={pl.name}
					            >
					                <EntityDescription>
					                    {pl.image && (
					                        <img
					                            style={{
					                                maxWidth: '100%',
					                                height: 'auto',
					                                objectFit: 'contain',
					                            }}
					                            src={pl.image.asset.url}
					                            alt=""
					                        />
					                    )}
					                    {pl.link && (
					                        <LinkButton
					                            link={pl.link}
					                            text={pl.name}
					                        />
					                    )}
					                </EntityDescription>
					            </Entity>
					        );
					    }
					})}
            </>
        )}
    </>
);

export default PointEntities;
